import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Play, Pause, Headphones, ListMusic, SkipForward } from "lucide-react";
import api from "@/lib/api";

const mediaUrl = (path: string) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

const playedSlugs = new Set<string>();
const trackPlay = (slug?: string) => {
  if (!slug || playedSlugs.has(slug)) return;
  playedSlugs.add(slug);
  api.post("/track/audio-play", { slug }).catch(() => {});
};

export const AudioBriefPlayer = ({ src, title, sponsor, slug }: {
  src: string; title?: string; slug?: string;
  sponsor?: { advertiser?: string; target_url?: string; ad_id?: string } | null;
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); trackPlay(slug); }
  };

  return (
    <div data-testid="audio-brief-player"
      className="flex items-center gap-3 bg-[#151022] border border-[#7C3AED]/50 rounded-xl px-4 py-3">
      <button onClick={toggle} data-testid="audio-play-button"
        className="flex items-center justify-center h-10 w-10 rounded-full bg-[#7C3AED] text-white hover:bg-[#6b2fd6] transition-colors shrink-0">
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#A78BFA]">
          <Headphones className="h-3 w-3" /> The Daily Brief · 60 seconds
        </p>
        <p className="text-white text-sm font-semibold truncate">{title || "Daily Brief"}</p>
        {sponsor?.advertiser && (
          <p className="font-mono text-[9px] uppercase tracking-widest mt-0.5 truncate">
            {sponsor.target_url ? (
              <a href={sponsor.target_url} target="_blank" rel="noreferrer sponsored"
                data-testid="brief-sponsor-credit"
                onClick={() => { if (sponsor.ad_id) api.post(`/ads/${sponsor.ad_id}/click`).catch(() => {}); }}
                className="text-[#F59E0B] hover:text-[#FBBF24] transition-colors">
                Presented by {sponsor.advertiser}
              </a>
            ) : (
              <span data-testid="brief-sponsor-credit" className="text-[#F59E0B]">
                Presented by {sponsor.advertiser}
              </span>
            )}
          </p>
        )}
        <div className="h-1 bg-white/10 rounded-full mt-1.5 overflow-hidden">
          <div className="h-full bg-[#7C3AED] rounded-full" style={{ width: `${progress}%`, transition: "width 300ms linear" }} />
        </div>
      </div>
      <audio ref={audioRef} src={mediaUrl(src)} preload="none"
        onTimeUpdate={(e) => {
          const a = e.target as HTMLAudioElement;
          if (a.duration) setProgress((a.currentTime / a.duration) * 100);
        }}
        onEnded={() => { setPlaying(false); setProgress(0); }} />
    </div>
  );
};

export const BriefPlaylist = ({ tracks }: { tracks: { src: string; title: string; slug?: string }[] }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [idx, setIdx] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (idx == null) return;
    setProgress(0);
    const a = audioRef.current;
    if (a) a.play().then(() => { setPlaying(true); trackPlay(tracks[idx]?.slug); }).catch(() => setPlaying(false));
  }, [idx]);

  if (!tracks.length) return null;
  const cur = idx != null ? tracks[idx] : null;

  const toggle = () => {
    if (idx == null) { setIdx(0); return; }
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  return (
    <div data-testid="brief-playlist" className="bg-[#151022] border border-[#7C3AED]/50 rounded-xl px-4 py-3.5 mb-12">
      <div className="flex items-center gap-3">
        <button onClick={toggle} data-testid="brief-playlist-play"
          className="flex items-center justify-center h-11 w-11 rounded-full bg-[#7C3AED] text-white hover:bg-[#6b2fd6] transition-colors shrink-0">
          {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </button>
        <div className="min-w-0 flex-1">
          {cur ? (
            <>
              <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#A78BFA]">
                <ListMusic className="h-3 w-3" /> Now playing · {(idx ?? 0) + 1} of {tracks.length}
              </p>
              <p className="text-white text-sm font-semibold truncate" data-testid="brief-playlist-current">{cur.title}</p>
              <div className="h-1 bg-white/10 rounded-full mt-1.5 overflow-hidden">
                <div className="h-full bg-[#7C3AED] rounded-full" style={{ width: `${progress}%`, transition: "width 300ms linear" }} />
              </div>
            </>
          ) : (
            <>
              <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#A78BFA]">
                <ListMusic className="h-3 w-3" /> This week in audio
              </p>
              <p className="text-white text-sm font-semibold">
                Play all {tracks.length} brief{tracks.length === 1 ? "" : "s"} back to back — about {tracks.length} minute{tracks.length === 1 ? "" : "s"}.
              </p>
            </>
          )}
        </div>
        {cur && (idx ?? 0) < tracks.length - 1 && (
          <button onClick={() => setIdx((i) => Math.min((i ?? 0) + 1, tracks.length - 1))}
            data-testid="brief-playlist-next" aria-label="Next brief"
            className="text-[#A78BFA] hover:text-white transition-colors shrink-0">
            <SkipForward className="h-5 w-5" />
          </button>
        )}
      </div>
      {cur && (
        <audio ref={audioRef} src={mediaUrl(cur.src)} preload="auto"
          onTimeUpdate={(e) => {
            const a = e.target as HTMLAudioElement;
            if (a.duration) setProgress((a.currentTime / a.duration) * 100);
          }}
          onEnded={() => {
            if ((idx ?? 0) < tracks.length - 1) setIdx((i) => (i ?? 0) + 1);
            else { setIdx(null); setPlaying(false); setProgress(0); }
          }} />
      )}
    </div>
  );
};

export const AudioBriefStrip = () => {
  const { data } = useQuery({
    queryKey: ["latest-brief"],
    queryFn: async () => (await api.get("/articles?content_type=market_brief&limit=1")).data,
    staleTime: 10 * 60 * 1000,
  });
  const brief = data?.items?.[0];
  if (!brief?.audio_url) return null;
  return (
    <div className="mb-6" data-testid="audio-brief-strip">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex-1 min-w-0">
          <AudioBriefPlayer src={brief.audio_url} title={brief.title} />
        </div>
        <Link to={`/article/${brief.slug}`} data-testid="audio-brief-read-link"
          className="font-mono text-[11px] uppercase tracking-widest text-[#A78BFA] hover:text-white transition-colors sm:pl-2 shrink-0">
          Read it instead →
        </Link>
      </div>
    </div>
  );
};
